import { useMutation } from "@tanstack/react-query";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { PREPOP_ENDPOINT_URL } from "../../constants/constants";
import type { Tokens } from "../../pages";
import { snakeCaseKeys, transformAnswers } from "../../utils";
import { useLocale } from "../useLocale";
import { useTokensValues } from "../tokens";
import { useMutationStatus } from "../useMutationStatus";
import { useQuestionnaire, type Answers } from "../../features/questionnaire";
import { questionnaireReducerAtom } from "../../features/questionnaire/state/questionnaireReducerAtom";

export type PrepopulateResponse = {
  url: string;
};

function prepopulateAnswersApi(
  config?: Record<string, unknown>,
  tokens?: Tokens,
) {
  return async function prepopulateAnswers(
    answers: Answers,
  ): Promise<PrepopulateResponse> {
    const isCommercial = answers.landlord_type !== "residential";
    const vertical = isCommercial ? "commercial_landlord" : config?.vertical;
    // Convert body to snake_case
    const body = snakeCaseKeys({
      ...(tokens ?? {}),
      ...(config ?? {}),
      vertical,
      partial_proposal: answers,
    });

    return fetch(PREPOP_ENDPOINT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json; charset=utf-8",
      },
      body: JSON.stringify(body),
    }).then(response => {
      if (!response.ok) {
        return Promise.reject(new Error("Failed to prepopulate answers"));
      }

      return response.json() as Promise<PrepopulateResponse>;
    });
  };
}

export function usePrepopulateAnswers() {
  const { config } = useQuestionnaire();
  const { locale } = useLocale();
  const tokens = useTokensValues();
  const isPrepopulatingAnswers = useMutationStatus("prepopulateAnswers");

  const { mutateAsync } = useMutation({
    mutationKey: ["prepopulateAnswers"],
    mutationFn: prepopulateAnswersApi(config, tokens),
  });

  // Read sections and visibility without subscribing to state changes
  const getQuestionnaireState = useAtomCallback(
    useCallback(get => get(questionnaireReducerAtom), []),
  );

  const prepopulateAnswers = async (answers: Answers) => {
    const state = getQuestionnaireState();
    const transformedAnswers = transformAnswers(
      answers,
      state.sections,
      state.visibility,
      locale,
    );

    await mutateAsync(transformedAnswers).then(response => {
      if (response.url) {
        window.location.replace(response.url);
      }
    });
  };

  return {
    prepopulateAnswers,
    isPending: isPrepopulatingAnswers,
  };
}
